import { IconButton, Button, TextField, FormControlLabel, Switch, Box, CircularProgress } from "@mui/material";
import { FC, useState, useEffect } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from "@tanstack/react-query";

export const EditConfirm: FC<{
  title: string;
  message: string;
  actionIcon: React.ReactNode;
  initialTitle?: string;
  initialIsPrivate?: boolean;
  onConfirm: (title: string, isPrivate: boolean) => void;
  onCancel: () => void;
  actionColor?: "error" | "primary" | "success";
}> = ({ 
  title, 
  message, 
  actionIcon, 
  initialTitle = "", 
  initialIsPrivate = true,
  onConfirm, 
  onCancel, 
  actionColor = "primary" 
}) => {
  const [countdown, setCountdown] = useState(2);
  const [canConfirm, setCanConfirm] = useState(false);
  const [editTitle, setEditTitle] = useState(initialTitle);
  const [isPrivate, setIsPrivate] = useState(initialIsPrivate);
  const { mutateAsync: editBookmark, isPending: isEditing } = useMutation({
    mutationFn: async () => {
      await onConfirm(editTitle, isPrivate);
    },
  });
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanConfirm(true);
    }
  }, [countdown]);

  const handleConfirm = () => {
    editBookmark();
  };

  return (
    <div className={`px-6 py-4 min-w-[480px] rounded-lg transition-all duration-300 ${
      isEditing ? 'bg-gray-300' : ''
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-semibold text-brand-1 flex-1">
          {title}
        </div>
        <IconButton
          size="small"
          onClick={onCancel}
          className="ml-2 text-neutral-700 hover:bg-neutral-100"
          disabled={isEditing}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      
      <div className="text-base text-neutral-900 mb-5 font-medium">
        {message}
      </div>

      {/* Title Edit Field */}
      <div className="mb-5">
        <TextField
          fullWidth
          label="Bookmark Title"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          variant="outlined"
          size="small"
          disabled={isEditing}
          className="[&_.MuiOutlinedInput-root]:text-sm [&_.MuiInputLabel-root]:text-sm"
        />
      </div>
      <div className="mb-5">
        <FormControlLabel
          control={
            <Switch
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              color="primary"
              disabled={isEditing}
            />
          }
          label={
            <Box>
              <div className="text-sm font-medium">
                {isPrivate ? 'Private' : 'Public'}
              </div>
              <div className="text-xs text-gray-600">
                {isPrivate 
                  ? 'Only you are allowed to view this folder' 
                  : 'All members of your team can view this folder.'
                }
              </div>
            </Box>
          }
          className="items-start ml-0 [&_.MuiFormControlLabel-label]:ml-2"
        />
      </div>
      {isEditing && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-[#7fa4ca] text-white rounded-lg animate-pulse">
          <CircularProgress size={18} sx={{ color: 'white' }} />
          <span className="text-sm font-medium">Saving your changes...</span>
        </div>
      )}
      <div className="flex items-center justify-end gap-3">
        <Button
          size="small"
          variant="outlined"
          onClick={onCancel}
          className="text-[0.813rem] px-2"
          disabled={isEditing}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color={actionColor}
          onClick={handleConfirm}
          disabled={!canConfirm || !editTitle.trim() || isEditing}
          startIcon={!isEditing ? actionIcon : null}
          className={`text-[0.813rem] px-2 transition-opacity duration-300 ease ${
            canConfirm && editTitle.trim() && !isEditing ? 'opacity-100' : 'opacity-40'
          }`}
        >
          {isEditing ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};
